import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { showAlert } from '../../appToast/appToastSlice';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IDeleteSpendAndStashResponse {
  status?: boolean;
  message?: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IDeleteSpendAndStashResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const deleteSpendAndStashAsync = createAsyncThunk(
  'savings/spendAndStash/delete',
  async (id: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.delete<IDeleteSpendAndStashResponse>(
        `/savings/spend-and-stash-configuration/${id}`,
        getSavingsAuthConfig(state),
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Spend & Stash configuration removed',
          type: 'drawer',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IDeleteSpendAndStashResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description: getApiErrorMessage(error.response?.data),
          type: 'drawer',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const deleteSpendAndStashSlice = createSlice({
  name: 'savings/spendAndStash/delete',
  initialState,
  reducers: {
    resetDeleteSpendAndStash(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteSpendAndStashAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(deleteSpendAndStashAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IDeleteSpendAndStashResponse;
      })
      .addCase(deleteSpendAndStashAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IDeleteSpendAndStashResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetDeleteSpendAndStash } = deleteSpendAndStashSlice.actions;

export default deleteSpendAndStashSlice.reducer;
